import React from 'react'

import '../../styles/raw-telemetry-analytics.css'

const formatSeconds = value => {
  if (!Number.isFinite(Number(value))) return '—'
  return `${Number(value) >= 0 ? '+' : ''}${Number(value).toFixed(3)} s`
}

const formatValue = (value, unit) => {
  if (!Number.isFinite(Number(value))) return '—'
  const decimals = unit === 'rpm' || unit === '' ? 0 : 1
  return `${Number(value).toFixed(decimals)}${unit ? ` ${unit}` : ''}`
}

const LapProgression = ({ laps, referenceLap }) => {
  const values = laps.map(lap => lap.timeSeconds).filter(Number.isFinite)
  const fastest = values.length ? Math.min(...values) : 0
  const slowest = values.length ? Math.max(...values) : fastest + 1
  const range = slowest - fastest || 1
  return (
    <div className="raw-analytics__laps">
      {laps.map(lap => (
        <div className={lap.number === referenceLap ? 'is-reference' : ''} key={lap.number}>
          <span>Lap {lap.number}</span>
          <div><i style={{ width: `${45 + ((lap.timeSeconds - fastest) / range) * 55}%` }} /></div>
          <strong>{lap.label}</strong>
          <small>{lap.number === referenceLap ? 'Reference' : lap.status}</small>
        </div>
      ))}
    </div>
  )
}

const ChannelRanges = ({ channels }) => (
  <div className="raw-analytics__channels">
    {channels.map(channel => {
      const range = channel.maximum - channel.minimum || 1
      const position = Math.max(0, Math.min(100, ((channel.average - channel.minimum) / range) * 100))
      return (
        <article key={channel.key}>
          <header><span>{channel.label}</span><small>{channel.sampleCount.toLocaleString()} samples</small></header>
          <div className="raw-analytics__range"><i style={{ left: `${position}%` }} /></div>
          <footer><span>{formatValue(channel.minimum, channel.unit)}</span><strong>{formatValue(channel.average, channel.unit)} average</strong><span>{formatValue(channel.maximum, channel.unit)}</span></footer>
        </article>
      )
    })}
  </div>
)

const SectionComparison = ({ sections, comparisonLaps }) => (
  <div className="raw-analytics__table-wrap">
    <table>
      <thead><tr><th>Section</th>{comparisonLaps.map(lap => <th key={lap}>Lap {lap}</th>)}</tr></thead>
      <tbody>
        {sections.map(section => (
          <tr key={section.number}>
            <th>{section.label}</th>
            {comparisonLaps.map(lap => {
              const comparison = section.comparisons.find(item => item.lapNumber === lap)
              const delta = comparison?.deltaSeconds
              return <td className={delta > 0 ? 'is-loss' : delta < 0 ? 'is-gain' : ''} data-label={`Lap ${lap}`} key={lap}>{Number.isFinite(delta) ? formatSeconds(delta) : '—'}</td>
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const RawTelemetryAnalytics = ({ analytics }) => {
  if (!analytics) return null
  return (
    <section className="raw-analytics" aria-labelledby={`${analytics.source}-raw-analytics-title`}>
      <header className="raw-analytics__heading">
        <div>
          <p>DEEPER SESSION ANALYSIS</p>
          <h2 id={`${analytics.source}-raw-analytics-title`}>Find the time in every lap</h2>
        </div>
        <p>Compare lap progression, input ranges and section deltas to see where pace changed and what you can repeat.</p>
      </header>

      <div className="raw-analytics__grid">
        <article className="raw-analytics__panel raw-analytics__panel--laps">
          <div className="raw-analytics__panel-heading"><div><span>Lap progression</span><h3>Every recorded lap</h3></div><small>Reference lap {analytics.referenceLap}</small></div>
          <LapProgression laps={analytics.laps} referenceLap={analytics.referenceLap} />
        </article>

        <article className="raw-analytics__panel raw-analytics__panel--channels">
          <div className="raw-analytics__panel-heading"><div><span>Channel ranges</span><h3>Measured inputs and outputs</h3></div><small>{analytics.sampleCount.toLocaleString()} plotted samples</small></div>
          <ChannelRanges channels={analytics.channels} />
        </article>

        <article className="raw-analytics__panel raw-analytics__panel--sections">
          <div className="raw-analytics__panel-heading"><div><span>Section comparison</span><h3>Where each lap gained or lost time</h3></div><small>Compared with the session reference</small></div>
          <SectionComparison sections={analytics.sections} comparisonLaps={analytics.comparisonLaps} />
        </article>
      </div>
    </section>
  )
}

export default RawTelemetryAnalytics
